import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  TextField,
  Grid,
  InputAdornment,
  Alert,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Autocomplete,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import AdHierarchySidebar from '../../components/AdHierarchySidebar';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adsetsService, AdSet } from '../../services/adsets.service';
import { errorMessage } from '../../utils/errorMessage';
import { campaignsService, Campaign } from '../../services/campaigns.service';
import { adAccountsService } from '../../services/ad-accounts.service';
import { createAdSetSchema, CreateAdSetFormData } from '../../schemas/adset.schema';
import {
  PERFORMANCE_GOALS,
  CONVERSION_EVENTS,
  ATTRIBUTION_SETTINGS,
  LANGUAGES,
  BID_STRATEGIES,
  normalizeObjective,
  type PerformanceGoal,
} from '../../constants/meta-odax';


// Common countries for targeting
const countries = [
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BR', name: 'Brazil' },
  { code: 'MX', name: 'Mexico' },
  { code: 'IN', name: 'India' },
  { code: 'JP', name: 'Japan' },
  { code: 'KR', name: 'South Korea' },
  { code: 'SG', name: 'Singapore' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'PH', name: 'Philippines' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'TH', name: 'Thailand' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'PL', name: 'Poland' },
  { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' },
  { code: 'FI', name: 'Finland' },
  { code: 'IE', name: 'Ireland' },
  { code: 'NZ', name: 'New Zealand' },
];



export default function CreateAdSetPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id: editId } = useParams<{ id: string }>();
  const isEditMode = !!editId;
  const queryClient = useQueryClient();
  const [selectedCountries, setSelectedCountries] = useState<typeof countries>([]);
  const [selectedLanguages, setSelectedLanguages] = useState<typeof LANGUAGES[number][]>([]);
  const [ageMin, setAgeMin] = useState(18);
  const [ageMax, setAgeMax] = useState(65);
  const [selectedInterests, setSelectedInterests] = useState<Array<{ id: string; name: string; path?: string[] }>>([]);
  const [interestSearch, setInterestSearch] = useState('');
  const [advantageAudience, setAdvantageAudience] = useState(true);
  const [budgetType, setBudgetType] = useState<'daily' | 'lifetime'>('daily');
  const [bidStrategy, setBidStrategy] = useState('LOWEST_COST_WITHOUT_CAP');
  // Dataset (pixel) & conversion event state
  const [datasetId, setDatasetId] = useState<string>('');
  const [conversionEvent, setConversionEvent] = useState<string>('PURCHASE');
  // One is auto-selected on mount, so the value cannot say whether anyone chose it.
  const [pixelChosen, setPixelChosen] = useState(false);
  const [attributionIdx, setAttributionIdx] = useState<number>(0); // 7d click + 1d view

  const preselectedCampaignId = searchParams.get('campaign_id') || '';

  // Fetch existing ad set when editing
  const { data: existingAdSet, isFetching: isFetchingAdSet } = useQuery({
    queryKey: ['adset', editId],
    queryFn: () => adsetsService.getById(editId!),
    enabled: isEditMode,
    // No placeholderData — clean transition when switching entities
  });

  // Pre-fill form from existing ad set
  const [initialized, setInitialized] = useState<string | false>(false);
  useEffect(() => {
    if (!existingAdSet || initialized === editId) return;
    setValue('campaign_id', existingAdSet.campaign_id);
    setValue('name', existingAdSet.name);
    setValue('status', existingAdSet.status as any);
    setValue('optimization_goal', existingAdSet.optimization_goal as any);
    if (existingAdSet.lifetime_budget && Number(existingAdSet.lifetime_budget) > 0) {
      setBudgetType('lifetime');
      setValue('lifetime_budget', Number(existingAdSet.lifetime_budget) as any);
    } else if (existingAdSet.daily_budget) {
      setBudgetType('daily');
      setValue('daily_budget', Number(existingAdSet.daily_budget) as any);
    }
    if (existingAdSet.bid_amount) setValue('bid_amount', Number(existingAdSet.bid_amount) as any);
    if (existingAdSet.start_time) setValue('start_time', existingAdSet.start_time.split('T')[0] as any);
    if (existingAdSet.end_time) setValue('end_time', existingAdSet.end_time.split('T')[0] as any);

    // Targeting
    const t = existingAdSet.targeting as any || {};
    if (t.geo_locations?.countries) {
      setSelectedCountries(countries.filter(c => t.geo_locations.countries.includes(c.code)));
    }
    if (t.locales) {
      setSelectedLanguages(LANGUAGES.filter(l => t.locales.includes(l.code)));
    }
    if (t.age_min) setAgeMin(t.age_min);
    if (t.age_max) setAgeMax(t.age_max);
    if (t.flexible_spec) {
      const interests = t.flexible_spec?.[0]?.interests || [];
      setSelectedInterests(interests);
    }
    if (t.targeting_automation) {
      setAdvantageAudience(!!t.targeting_automation.advantage_audience);
    }

    // Budget & bid
    if (existingAdSet.bid_strategy) setBidStrategy(existingAdSet.bid_strategy);

    // Promoted object / dataset
    const po = existingAdSet.promoted_object;
    if (po?.pixel_id) setDatasetId(po.pixel_id);
    if (po?.custom_event_type) setConversionEvent(po.custom_event_type);

    // Attribution
    if (existingAdSet.attribution_spec) {
      const matchIdx = ATTRIBUTION_SETTINGS.findIndex(s =>
        JSON.stringify(s.spec) === JSON.stringify(existingAdSet.attribution_spec)
      );
      if (matchIdx >= 0) setAttributionIdx(matchIdx);
    }

    setInitialized(editId || false);
  }, [existingAdSet, editId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch campaigns for selection
  const { data: campaignsResult, isLoading: isLoadingCampaigns } = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => campaignsService.query(),
  });

  const campaigns: Campaign[] = campaignsResult?.data ?? [];

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateAdSetFormData>({
    resolver: yupResolver(createAdSetSchema),
    defaultValues: {
      campaign_id: preselectedCampaignId,
      name: '',
      status: 'PAUSED',
      optimization_goal: undefined,
      billing_event: 'IMPRESSIONS',
      daily_budget: undefined,
      lifetime_budget: undefined,
      bid_amount: undefined,
      start_time: undefined,
      end_time: undefined,
      targeting: {},
    },
  });

  // Performance goal options driven by campaign objective
  const selectedCampaignId = watch('campaign_id');
  const selectedCampaign = campaigns.find((c) => c.id === selectedCampaignId);
  const objective = normalizeObjective(selectedCampaign?.objective || 'TRAFFIC');
  const performanceGoals: PerformanceGoal[] = PERFORMANCE_GOALS[objective] || PERFORMANCE_GOALS.TRAFFIC;
  const selectedGoalValue = watch('optimization_goal');
  const selectedGoal = performanceGoals.find((g) => g.value === selectedGoalValue);
  const needsDataset = !!selectedGoal?.requiresDataset;

  // Auto-select the first valid goal when campaign changes
  useEffect(() => {
    if (!selectedGoalValue || !performanceGoals.find((g) => g.value === selectedGoalValue)) {
      if (performanceGoals.length > 0) setValue('optimization_goal', performanceGoals[0].value as CreateAdSetFormData['optimization_goal']);
    }
  }, [objective]); // eslint-disable-line react-hooks/exhaustive-deps

  // Interest search (debounced via React Query)
  const { data: interestOptions = [] } = useQuery({
    queryKey: ['interest-search', selectedCampaign?.ad_account_id, interestSearch],
    queryFn: () => adAccountsService.searchInterests(selectedCampaign!.ad_account_id!, interestSearch),
    enabled: !!selectedCampaign?.ad_account_id && interestSearch.length >= 2,
  });

  // Fetch datasets (pixels) — recommended for all goals, required for conversion goals
  const { data: datasets = [] } = useQuery({
    queryKey: ['pixels', selectedCampaign?.ad_account_id],
    queryFn: () => adAccountsService.listPixels(selectedCampaign!.ad_account_id!),
    enabled: !!selectedCampaign?.ad_account_id,
  });

  // Only for goals that require a dataset — see the conversion-only note in onSubmit.
  useEffect(() => {
    if (needsDataset && datasets.length > 0 && !datasetId) {
      setDatasetId(datasets[0].id);
    }
  }, [datasets, needsDataset]); // eslint-disable-line react-hooks/exhaustive-deps

  const createMutation = useMutation({
    mutationFn: (payload: Partial<AdSet>) =>
      isEditMode ? adsetsService.update(editId!, payload) : adsetsService.create(payload),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['adsets'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-adsets'] });
      queryClient.invalidateQueries({ queryKey: ['adset', editId] });
      // Stay in edit mode; only navigate on create
      if (!isEditMode) navigate(`/adsets/${result.id}/edit`);
    },
  });

  const alreadyStarted = !!(isEditMode && existingAdSet?.fb_adset_id && existingAdSet?.start_time && new Date(existingAdSet.start_time) < new Date());
  const formData = watch();

  const onSubmit = (data: CreateAdSetFormData) => {
    // Build targeting
    const targeting: Record<string, unknown> = {};
    if (selectedCountries.length > 0) {
      targeting.geo_locations = { countries: selectedCountries.map(c => c.code) };
    }
    if (selectedLanguages.length > 0) {
      targeting.locales = selectedLanguages.map(l => l.code);
    }
    targeting.age_min = ageMin;
    targeting.age_max = ageMax;
    if (selectedInterests.length > 0) {
      targeting.flexible_spec = [{ interests: selectedInterests.map(i => ({ id: i.id, name: i.name })) }];
    }
    targeting.targeting_automation = { advantage_audience: advantageAudience ? 1 : 0 };

    const needsBidAmount = bidStrategy === 'LOWEST_COST_WITH_BID_CAP' || bidStrategy === 'COST_CAP';

    // Conversion-goal-only: FB rejects a conversion pixel / view-through window on a traffic goal.
    // Gate on needsDataset (not just datasetId) so an auto-selected pixel doesn't linger on switch.
    const promotedObject = (needsDataset && datasetId)
      ? { pixel_id: datasetId, custom_event_type: conversionEvent }
      : undefined;
    // Also never on update — FB rejects attribution_spec changes for some goal combos.
    const attributionSpec = (!isEditMode && needsDataset && datasetId)
      ? ATTRIBUTION_SETTINGS[attributionIdx].spec.map((s) => ({ ...s }))
      : undefined;

    const payload: Partial<AdSet> = {
      campaign_id: data.campaign_id,
      name: data.name,
      optimization_goal: data.optimization_goal || performanceGoals[0]?.value,
      bid_strategy: bidStrategy,
      daily_budget: data.daily_budget ? Number(data.daily_budget) : undefined,
      lifetime_budget: data.lifetime_budget ? Number(data.lifetime_budget) : undefined,
      bid_amount: needsBidAmount && data.bid_amount ? Number(data.bid_amount) : undefined,
      start_time: (isEditMode && existingAdSet?.fb_adset_id && existingAdSet?.start_time)
        ? undefined  // can't change start_time on published ad sets that already started
        : (data.start_time ? new Date(data.start_time).toISOString() : undefined),
      end_time: data.end_time ? new Date(data.end_time).toISOString() : undefined,
      targeting,
      // Only when touched: otherwise a rename ships a pixel Facebook never had.
      ...(promotedObject && (!isEditMode || pixelChosen) && { promoted_object: promotedObject }),
      ...(attributionSpec && { attribution_spec: attributionSpec }),
      // Set once, at creation; resending either on an edit fails with an error nobody asked for.
      ...(isEditMode ? {} : { billing_event: 'IMPRESSIONS', destination_type: 'WEBSITE' }),
    };
    createMutation.mutate(payload);
  };

  const handleSave = () => {
    handleSubmit(onSubmit)();
  };

  const handleCancel = () => {
    if (preselectedCampaignId) {
      navigate(`/campaigns/${preselectedCampaignId}`);
    } else {
      navigate('/adsets');
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 0, mx: -3, mt: -3, minHeight: 'calc(100vh - 120px)' }}>
      {/* Hierarchy sidebar — show when we have a campaign context */}
      {(isEditMode || preselectedCampaignId || watch('campaign_id')) && (
        <AdHierarchySidebar
          campaignId={watch('campaign_id') || undefined}
          adSetId={editId}
        />
      )}

      <Box sx={{ flex: 1, p: 3, overflow: 'auto', maxWidth: 900, opacity: (isEditMode && isFetchingAdSet && initialized !== editId) ? 0.4 : 1, transition: 'opacity 0.15s' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button startIcon={<ArrowBack />} onClick={handleCancel}>
            Back
          </Button>
          <Typography variant="h4" fontWeight={600} sx={{ ml: 2 }}>
            {isEditMode ? 'Edit Ad Set' : 'Create Ad Set'}
          </Typography>
        </Box>

        <Card>
        <CardContent>
          {createMutation.error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errorMessage(createMutation.error, `Failed to ${isEditMode ? 'update' : 'create'} ad set. Please try again.`)}
            </Alert>
          )}

          {/* Section: Campaign & Goals */}
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Campaign & Goals</Typography>
            <Grid container spacing={3}>
              {campaigns.length === 0 && !isLoadingCampaigns && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning">
                    No campaigns found. Please create a campaign first.
                  </Alert>
                </Grid>
              )}
              <Grid size={{ xs: 12 }}>
                <Controller
                  name="campaign_id"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={campaigns}
                      getOptionLabel={(o) => o.name}
                      value={campaigns.find(c => c.id === field.value) || null}
                      onChange={(_, v) => field.onChange(v?.id || '')}
                      // Moving it here would only move it in Neptune.
                      disabled={isLoadingCampaigns || !!preselectedCampaignId || isEditMode}
                      loading={isLoadingCampaigns}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Campaign"
                          error={!!errors.campaign_id}
                          helperText={errors.campaign_id?.message}
                        />
                      )}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Ad Set Name"
                      error={!!errors.name}
                      helperText={errors.name?.message}
                    />
                  )}
                />
              </Grid>
              {/* Conversion location — always Website for us */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Conversion location"
                  value="Website"
                  disabled
                  helperText="Where conversions happen"
                />
              </Grid>

              {/* Performance goal (maps to API optimization_goal) */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="optimization_goal"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={performanceGoals}
                      getOptionLabel={(o) => o.label}
                      value={performanceGoals.find(g => g.value === field.value) || null}
                      onChange={(_, v) => field.onChange(v?.value || '')}
                      renderInput={(params) => <TextField {...params} label="Performance goal" />}
                    />
                  )}
                />
              </Grid>

              {/* Dataset / conversion event / attribution — conversion goals only */}
              {needsDataset && datasets.length > 0 && (
                <>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Autocomplete
                      options={datasets}
                      getOptionLabel={(o) => `${o.name}${o.is_unavailable ? ' (unavailable)' : ''}`}
                      value={datasets.find(d => d.id === datasetId) || null}
                      onChange={(_, v) => { setDatasetId(v?.id || ''); setPixelChosen(true); }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Dataset (pixel) *"
                          helperText="Required for this performance goal"
                        />
                      )}
                    />
                  </Grid>
                  {datasetId && (
                    <>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Autocomplete
                          options={[...CONVERSION_EVENTS]}
                          getOptionLabel={(o) => o.label}
                          value={CONVERSION_EVENTS.find(e => e.value === conversionEvent) || null}
                          onChange={(_, v) => { setConversionEvent(v?.value || 'PURCHASE'); setPixelChosen(true); }}
                          renderInput={(params) => <TextField {...params} label="Conversion event" />}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Autocomplete
                          options={ATTRIBUTION_SETTINGS.map((w, i) => ({ label: w.label, idx: i }))}
                          getOptionLabel={(o) => o.label}
                          value={{ label: ATTRIBUTION_SETTINGS[attributionIdx].label, idx: attributionIdx }}
                          onChange={(_, v) => setAttributionIdx(v?.idx ?? 0)}
                          isOptionEqualToValue={(o, v) => o.idx === v.idx}
                          renderInput={(params) => <TextField {...params} label="Attribution setting" />}
                        />
                      </Grid>
                    </>
                  )}
                </>
              )}
            </Grid>

          {/* Section: Targeting */}
          <Typography variant="h6" fontWeight={600} sx={{ mt: 4, mb: 2 }}>Audience Targeting</Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Define who sees your ads
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select the countries where your ads will be shown. At least one location is required.
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  multiple
                  options={countries}
                  getOptionLabel={(option) => option.name}
                  value={selectedCountries}
                  onChange={(_, newValue) => setSelectedCountries(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Target Countries"
                      placeholder="Select countries..."
                      error={selectedCountries.length === 0}
                      helperText={selectedCountries.length === 0 ? "At least one country is required" : `${selectedCountries.length} countries selected`}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option.name}
                        {...getTagProps({ index })}
                        key={option.code}
                      />
                    ))
                  }
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  multiple
                  options={LANGUAGES as unknown as typeof LANGUAGES[number][]}
                  getOptionLabel={(option) => option.label}
                  value={selectedLanguages}
                  onChange={(_, newValue) => setSelectedLanguages(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Languages (optional)"
                      placeholder="Select languages..."
                      helperText={selectedLanguages.length > 0 ? `${selectedLanguages.length} languages selected` : 'Leave empty to target all languages'}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option.label}
                        {...getTagProps({ index })}
                        key={option.code}
                      />
                    ))
                  }
                />
              </Grid>
              {/* Advantage Audience */}
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={advantageAudience}
                      onChange={(e) => setAdvantageAudience(e.target.checked)}
                    />
                  }
                  label="Advantage Audience"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 7, mt: -0.5 }}>
                  {advantageAudience
                    ? 'Meta\'s AI expands beyond your targeting to find more conversions. Age, interests etc. become suggestions.'
                    : 'Your targeting constraints are strictly enforced.'}
                </Typography>
              </Grid>

              {/* Age range */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Age Range: {ageMin} – {ageMax}{advantageAudience ? ' (suggestion)' : ''}
                </Typography>
                <Slider
                  value={[ageMin, ageMax]}
                  onChange={(_, v) => { const [min, max] = v as number[]; setAgeMin(min); setAgeMax(max); }}
                  min={18}
                  max={65}
                  marks={[{ value: 18, label: '18' }, { value: 25, label: '25' }, { value: 35, label: '35' }, { value: 45, label: '45' }, { value: 55, label: '55' }, { value: 65, label: '65+' }]}
                  valueLabelDisplay="auto"
                />
              </Grid>

              {/* Interest targeting */}
              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  multiple
                  options={interestOptions.filter(o => !selectedInterests.find(s => s.id === o.id))}
                  getOptionLabel={(option) => option.name}
                  value={selectedInterests}
                  onChange={(_, newValue) => setSelectedInterests(newValue)}
                  inputValue={interestSearch}
                  onInputChange={(_, v) => setInterestSearch(v)}
                  filterOptions={(x) => x}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Interests (optional)"
                      placeholder="Search interests..."
                      helperText={selectedInterests.length > 0 ? `${selectedInterests.length} interests selected` : 'Type to search Facebook interests'}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option.name}
                        {...getTagProps({ index })}
                        key={option.id}
                      />
                    ))
                  }
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box>
                        <Typography variant="body2">{option.name}</Typography>
                        {option.path && (
                          <Typography variant="caption" color="text.secondary">
                            {option.path.join(' > ')}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  noOptionsText={interestSearch.length < 2 ? 'Type at least 2 characters...' : 'No interests found'}
                />
              </Grid>

              {selectedCountries.length > 0 && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="success">
                    Audience: {selectedCountries.map(c => c.name).join(', ')}
                    {selectedLanguages.length > 0 && ` · ${selectedLanguages.map(l => l.label).join(', ')}`}
                    {` · Age ${ageMin}–${ageMax}`}
                    {selectedInterests.length > 0 && ` · ${selectedInterests.length} interests`}
                    {` · Advantage Audience: ${advantageAudience ? 'On' : 'Off'}`}
                  </Alert>
                </Grid>
              )}
            </Grid>

          {/* Section: Budget & Schedule */}
          <Typography variant="h6" fontWeight={600} sx={{ mt: 4, mb: 2 }}>Budget & Schedule</Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <ToggleButtonGroup
                  value={budgetType}
                  exclusive
                  onChange={(_, v) => {
                    if (!v) return;
                    setBudgetType(v);
                    if (v === 'daily') setValue('lifetime_budget', undefined as any);
                    if (v === 'lifetime') setValue('daily_budget', undefined as any);
                  }}
                  size="small"
                >
                  <ToggleButton value="daily">Daily Budget</ToggleButton>
                  <ToggleButton value="lifetime">Lifetime Budget</ToggleButton>
                </ToggleButtonGroup>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name={budgetType === 'daily' ? 'daily_budget' : 'lifetime_budget'}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label={budgetType === 'daily' ? 'Daily Budget' : 'Lifetime Budget'}
                      type="number"
                      helperText={budgetType === 'daily' ? 'Budget spent per day' : 'Total budget for ad set lifetime'}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  options={[...BID_STRATEGIES]}
                  getOptionLabel={(o) => o.label}
                  value={BID_STRATEGIES.find(s => s.value === bidStrategy) || null}
                  onChange={(_, v) => setBidStrategy(v?.value || 'LOWEST_COST_WITHOUT_CAP')}
                  isOptionEqualToValue={(o, v) => o.value === v.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Bid Strategy"
                      helperText={BID_STRATEGIES.find(s => s.value === bidStrategy)?.description}
                    />
                  )}
                />
              </Grid>
              {(bidStrategy === 'LOWEST_COST_WITH_BID_CAP' || bidStrategy === 'COST_CAP') && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="bid_amount"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label={bidStrategy === 'COST_CAP' ? 'Cost Cap Amount' : 'Bid Cap Amount'}
                      type="number"
                      error={!!errors.bid_amount}
                      helperText={errors.bid_amount?.message || (bidStrategy === 'COST_CAP' ? 'Maximum average cost per result' : 'Maximum bid per auction')}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                    />
                  )}
                />
              </Grid>
              )}
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Schedule (Optional)
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="start_time"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Start Date"
                      type="datetime-local"
                      error={!!errors.start_time}
                      helperText={alreadyStarted ? 'Cannot change — ad set already started' : errors.start_time?.message}
                      disabled={alreadyStarted}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                  name="end_time"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="End Date"
                      type="datetime-local"
                      error={!!errors.end_time}
                      helperText={errors.end_time?.message || 'Required when using lifetime budget'}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
            </Grid>

          {/* Save Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleSave}
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? 'Saving...' : (isEditMode ? 'Save Changes' : 'Create Ad Set')}
            </Button>
          </Box>

          {false as boolean && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Review Your Ad Set
              </Typography>
              {selectedCountries.length === 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No target countries selected. Facebook requires at least one location.
                </Alert>
              )}
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Campaign
                  </Typography>
                  <Typography variant="body1">
                    {selectedCampaign?.name || '-'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Name
                  </Typography>
                  <Typography variant="body1">{formData.name}</Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" color="text.secondary">
                    Target Countries
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {selectedCountries.length > 0 ? (
                      selectedCountries.map((country) => (
                        <Chip key={country.code} label={country.name} size="small" />
                      ))
                    ) : (
                      <Typography variant="body1" color="error">None selected</Typography>
                    )}
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Optimization Goal
                  </Typography>
                  <Typography variant="body1">
                    {performanceGoals.find((g) => g.value === formData.optimization_goal)?.label || formData.optimization_goal}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Conversion location
                  </Typography>
                  <Typography variant="body1">Website</Typography>
                </Grid>
                {needsDataset && datasetId && (
                  <>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="body2" color="text.secondary">Dataset (pixel)</Typography>
                      <Typography variant="body1">{datasets.find(p => p.id === datasetId)?.name || datasetId}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="body2" color="text.secondary">Conversion event</Typography>
                      <Typography variant="body1">{conversionEvent}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="body2" color="text.secondary">Attribution</Typography>
                      <Typography variant="body1">{ATTRIBUTION_SETTINGS[attributionIdx].label}</Typography>
                    </Grid>
                  </>
                )}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Typography variant="body1">{formData.status}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Daily Budget
                  </Typography>
                  <Typography variant="body1">
                    {formData.daily_budget ? `$${formData.daily_budget}` : '-'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Lifetime Budget
                  </Typography>
                  <Typography variant="body1">
                    {formData.lifetime_budget ? `$${formData.lifetime_budget}` : '-'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Bid Strategy
                  </Typography>
                  <Typography variant="body1">
                    {BID_STRATEGIES.find((s) => s.value === bidStrategy)?.label}
                  </Typography>
                </Grid>
                {(bidStrategy === 'LOWEST_COST_WITH_BID_CAP' || bidStrategy === 'COST_CAP') && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    {bidStrategy === 'COST_CAP' ? 'Cost Cap' : 'Bid Cap'}
                  </Typography>
                  <Typography variant="body1">
                    {formData.bid_amount ? `$${formData.bid_amount}` : 'Not set'}
                  </Typography>
                </Grid>
                )}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Start Date
                  </Typography>
                  <Typography variant="body1">
                    {formData.start_time ? new Date(formData.start_time).toLocaleString() : 'Immediately'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    End Date
                  </Typography>
                  <Typography variant="body1">
                    {formData.end_time ? new Date(formData.end_time).toLocaleString() : 'No end date'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Old navigation removed — single save button above */}
        </CardContent>
      </Card>
      </Box>
    </Box>
  );
}
